import dexieService from './dexieService'
import apiStatusService from './apiStatusService'
import apiService from './api'

/**
 * Types de mutations supportés
 */
export const MutationType = {
  INSERT: 'INSERT',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE'
}

/**
 * Entités supportées pour les mutations
 */
export const MutationEntity = {
  PROPERTY: 'property',
  PROPERTY_TECHNICAL_MANAGER: 'property_technical_manager',
  PROPERTY_PLANIFICATION: 'property_planification',
  VISIT: 'visit',
  ACTION: 'action',
  CONTROL: 'control',
  COMMENT: 'comment',
  PRE_CHIFFRAGE: 'pre_chiffrage'
}

/**
 * Service de gestion des mutations avec synchronisation automatique
 * Gère le stockage local des modifications et leur synchronisation avec l'API
 */
class MutationService {
  constructor() {
    this.apiService = apiService
    this.dexieService = dexieService
    this.syncInProgress = false
    this.syncListeners = []
    this.lastSyncTimestamp = 0
    this.minSyncInterval = 2000 // Délai minimum de 2 secondes entre deux synchronisations
    
    // Écouter les changements d'état de l'API
    this.initializeApiStatusListener()
  }

  /**
   * Initialise l'écoute de l'état de l'API.
   * Réagit UNIQUEMENT à la transition offline → online pour rejouer les mutations
   * en attente et déclencher une sync immédiate de rattrapage.
   * La synchronisation périodique nominale est gérée par syncService.startPeriodicSync().
   */
  initializeApiStatusListener() {
    // addListener() émet immédiatement avec le statut courant (souvent false au démarrage,
    // avant le premier health check). On ignore ce premier appel pour ne pas enregistrer
    // un "previouslyAvailable = false" qui déclencherait une fausse transition offline→online
    // dès que le premier health check revient true.
    let isFirstCall = true
    let previouslyAvailable = false

    apiStatusService.addListener(async (isAvailable) => {
      if (isFirstCall) {
        isFirstCall = false
        previouslyAvailable = isAvailable
        return // Ignorer l'appel initial : ce n'est pas une vraie transition
      }

      const justCameOnline = isAvailable && previouslyAvailable !== true
      previouslyAvailable = isAvailable

      if (!justCameOnline) {
        // Pas de transition offline→online : rien à faire ici
        return
      }

      if (this.syncInProgress) {
        return
      }


      // Rejouer les mutations en attente puis déclencher une smartSync de rattrapage
      try {
        await this.syncPendingMutations()

        const syncService = (await import('./syncService')).default
        await syncService.smartSync()
      } catch (error) {
        console.error('[MutationService] Erreur lors de la sync au retour en ligne:', error)
      }
    })
  }

  /**
   * Vérifie si l'API est accessible
   * @returns {Promise<boolean>}
   */
  async isApiAccessible() {
    try {
      // Ping léger sans requête DB (GET /health)
      await this.apiService.publicRequest('/health')
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Exécute une mutation (INSERT, UPDATE, DELETE)
   * Tente d'envoyer à l'API, sinon stocke localement
   * 
   * @param {Object} params - Paramètres de la mutation
   * @param {string} params.entity - Type d'entité (MutationEntity)
   * @param {string} params.type - Type de mutation (MutationType)
   * @param {string|null} params.entityId - ID de l'entité (null pour INSERT)
   * @param {Object} params.data - Données de la mutation
   * @param {Function} params.apiCall - Fonction à appeler pour synchroniser avec l'API
   * @param {Function} params.localUpdate - Fonction à appeler pour mettre à jour les données locales
   * @returns {Promise<{success: boolean, synced: boolean, mutationId?: string, error?: string}>}
   */
  async executeMutation({ entity, type, entityId, data, apiCall, localUpdate }) {
    const mutationId = this.generateMutationId(entity, type, entityId)

    try {
      // Vérifier si l'API est accessible
      const isApiAccessible = await this.isApiAccessible()

      if (isApiAccessible) {
        // API accessible - tenter l'appel API directement
        // Si l'appel échoue, on propage l'erreur au lieu de stocker pour rejeu
        // pour éviter les doublons (l'API peut avoir réussi malgré une erreur côté client)
        const apiResult = await apiCall()

        // Succès de l'API - mettre à jour les données locales
        if (localUpdate) {
          await localUpdate(apiResult)
        }

        return { 
          success: true, 
          synced: true,
          mutationId,
          result: apiResult
        }
      } else {
        // API non accessible - stocker la mutation pour synchronisation ultérieure
        
        await this.storeMutation({
          id: mutationId,
          entity,
          type,
          entityId,
          data,
          apiCall: apiCall.toString()
        })

        // Mettre à jour les données locales avec flag de synchronisation
        if (localUpdate) {
          await localUpdate(null, true) // true = toSync
        }

        return { 
          success: true, 
          synced: false,
          mutationId
        }
      }

    } catch (error) {
      console.error('Erreur lors de l\'exécution de la mutation:', error)
      return { 
        success: false, 
        synced: false, 
        error: error.message 
      }
    }
  }

  /**
   * Génère un ID unique pour une mutation
   */
  generateMutationId(entity, type, entityId) {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 9)
    return `${entity}_${type}_${entityId || 'new'}_${timestamp}_${random}`
  }

  /**
   * Sérialise profondément un objet pour le stockage dans IndexedDB
   * Convertit les valeurs non sérialisables en valeurs sérialisables
   */
  deepSerialize(obj) {
    if (obj === null || obj === undefined) {
      return obj
    }

    // Types primitifs
    if (typeof obj !== 'object') {
      return obj
    }

    // Dates
    if (obj instanceof Date) {
      return obj.toISOString()
    }

    // Tableaux
    if (Array.isArray(obj)) {
      return obj.map(item => this.deepSerialize(item))
    }

    // Objets
    const serialized = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key]
        
        // Ignorer les fonctions
        if (typeof value === 'function') {
          continue
        }
        
        // Sérialiser récursivement
        serialized[key] = this.deepSerialize(value)
      }
    }
    
    return serialized
  }

  /**
   * Stocke une mutation dans IndexedDB pour synchronisation ultérieure
   * 
   * @param {Object} mutation - Données de la mutation
   * @param {string} mutation.id - ID unique de la mutation
   * @param {string} mutation.entity - Type d'entité
   * @param {string} mutation.type - Type de mutation (INSERT, UPDATE, DELETE)
   * @param {string|null} mutation.entityId - ID de l'entité (null pour INSERT)
   * @param {Object} mutation.data - Données de la mutation
   * @param {string} mutation.error - Message d'erreur éventuel
   * @param {string|null} mutation.propertyId - ID de la propriété associée (optionnel)
   */
  async storeMutation(mutation) {
    try {
      const { mutations } = await import('./dexieService')
      
      // Sérialiser profondément les données pour éviter DataCloneError
      const serializedData = this.deepSerialize(mutation.data)
      
      // Extraire propertyId depuis les données si non fourni directement
      const propertyId = mutation.propertyId || 
                        serializedData?.propertyId || 
                        serializedData?.property?.id || 
                        null
      
      const mutationRecord = {
        // Ne pas fournir d'ID, laisser Dexie auto-incrémenter (++id)
        attempts: 0,
        data: serializedData,
        entity: mutation.entity,
        entityId: mutation.entityId,
        insertedAt: new Date().toISOString(),
        lastAttempt: null,
        lastError: mutation.error || null,
        mutationId: mutation.id,
        synced: false,
        type: mutation.type,
        propertyId: propertyId
      }

      const generatedId = await mutations().add(mutationRecord)

      // Notifier les listeners avec l'ID généré
      this.notifySyncListeners({ type: 'mutation_stored', mutation: { ...mutationRecord, id: generatedId } })

      return true
    } catch (error) {
      console.error('Erreur lors du stockage de la mutation:', error)
      throw error
    }
  }

  /**
   * Récupère toutes les mutations en attente de synchronisation
   * Toutes les mutations sont en attente car elles sont supprimées après sync
   * @returns {Promise<Array>}
   */
  async getPendingMutations() {
    try {
      const { mutations } = await import('./dexieService')
      const pendingMutations = await mutations()
        .orderBy('insertedAt')
        .toArray()
      
      return pendingMutations
    } catch (error) {
      console.error('Erreur lors de la récupération des mutations en attente:', error)
      return []
    }
  }

  /**
   * Synchronise toutes les mutations en attente avec l'API
   * @param {boolean} force - Force la synchronisation même si déjà en cours
   * @returns {Promise<{success: boolean, syncedCount: number, totalCount: number, errors: Array}>}
   */
  async syncPendingMutations(force = false) {
    if (this.syncInProgress && !force) {
      return { success: false, syncedCount: 0, totalCount: 0, errors: ['Synchronisation déjà en cours'] }
    }

    this.syncInProgress = true
    this.notifySyncListeners({ type: 'sync_started' })

    try {
      // Récupérer les mutations en attente
      const pendingMutations = await this.getPendingMutations()

      if (pendingMutations.length === 0) {
        this.syncInProgress = false
        this.notifySyncListeners({ type: 'sync_completed', syncedCount: 0, totalCount: 0 })
        return { success: true, syncedCount: 0, totalCount: 0, errors: [] }
      }

      // Vérifier si l'API est accessible
      const isApiAccessible = await this.isApiAccessible()
      if (!isApiAccessible) {
        this.syncInProgress = false
        this.notifySyncListeners({ type: 'sync_failed', reason: 'API non accessible' })
        return { 
          success: false, 
          syncedCount: 0, 
          totalCount: pendingMutations.length, 
          errors: ['API non accessible'] 
        }
      }

      const errors = []
      let syncedCount = 0

      // Synchroniser chaque mutation
      for (const mutation of pendingMutations) {
        try {
          const result = await this.syncMutation(mutation)
          if (result.success) {
            syncedCount++
          } else {
            errors.push(`${mutation.id}: ${result.error}`)
          }
        } catch (error) {
          console.error(`Erreur lors de la sync de la mutation ${mutation.id}:`, error)
          errors.push(`${mutation.id}: ${error.message}`)
        }
      }

      this.syncInProgress = false
      this.notifySyncListeners({ 
        type: 'sync_completed', 
        syncedCount, 
        totalCount: pendingMutations.length,
        errors 
      })

      return {
        success: errors.length === 0,
        syncedCount,
        totalCount: pendingMutations.length,
        errors
      }

    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error)
      this.syncInProgress = false
      this.notifySyncListeners({ type: 'sync_failed', reason: error.message })
      
      return {
        success: false,
        syncedCount: 0,
        totalCount: 0,
        errors: [error.message]
      }
    }
  }

  /**
   * Synchronise une mutation individuelle avec l'API
   * @param {Object} mutation - La mutation à synchroniser
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async syncMutation(mutation) {
    const { mutations } = await import('./dexieService')

    try {
      // Incrémenter le compteur de tentatives
      mutation.attempts += 1
      mutation.lastAttempt = new Date().toISOString()

      let apiResult = null

      // Exécuter l'appel API selon le type d'entité
      switch (mutation.entity) {
        case MutationEntity.PROPERTY_TECHNICAL_MANAGER:
          apiResult = await this.syncPropertyTechnicalManager(mutation)
          break

        case MutationEntity.PROPERTY_PLANIFICATION:
          apiResult = await this.syncPropertyPlanification(mutation)
          break

        case MutationEntity.VISIT:
          apiResult = await this.syncVisit(mutation)
          break

        case MutationEntity.ACTION:
          apiResult = await this.syncAction(mutation)
          break

        case MutationEntity.CONTROL:
          apiResult = await this.syncControl(mutation)
          break

        case MutationEntity.COMMENT:
          apiResult = await this.syncComment(mutation)
          break

        case MutationEntity.PRE_CHIFFRAGE:
          apiResult = await this.syncPreChiffrage(mutation)
          break

        default:
          throw new Error(`Type d'entité non supporté: ${mutation.entity}`)
      }

      // Supprimer la mutation après synchronisation réussie
      await mutations().delete(mutation.id)

      // Mettre à jour l'entité locale pour supprimer le flag toSync
      await this.updateLocalEntityAfterSync(mutation, apiResult)

      return { success: true }

    } catch (error) {
      console.error(`Erreur lors de la synchronisation de la mutation ${mutation.id}:`, error)
      
      // Mettre à jour avec l'erreur
      mutation.lastError = error.message
      await mutations().put(mutation)

      return { success: false, error: error.message }
    }
  }

  /**
   * Synchronise une mutation de gestionnaire technique
   */
  async syncPropertyTechnicalManager(mutation) {
    const { entityId, data } = mutation

    if (mutation.type === MutationType.UPDATE) {
      return await this.apiService.request(`/properties/${entityId}/technical-manager`, {
        method: 'PUT',
        body: JSON.stringify({
          technicalManagerId: data.technicalManager?.id,
          teamId: data.team?.id
        })
      })
    }

    throw new Error(`Type de mutation non supporté pour PROPERTY_TECHNICAL_MANAGER: ${mutation.type}`)
  }

  /**
   * Synchronise une mutation de planification
   */
  async syncPropertyPlanification(mutation) {
    const { entityId, data } = mutation

    if (mutation.type === MutationType.UPDATE) {
      return await this.apiService.request(`/properties/${entityId}/planification`, {
        method: 'PUT',
        body: JSON.stringify({
          planifiedAt: data.planifiedAt
        })
      })
    }

    throw new Error(`Type de mutation non supporté pour PROPERTY_PLANIFICATION: ${mutation.type}`)
  }

  /**
   * Synchronise une mutation de visite
   */
  async syncVisit(mutation) {
    const { entityId, type, data } = mutation

    switch (type) {
      case MutationType.INSERT:
        return await this.apiService.createVisit(data)
      
      case MutationType.UPDATE:
        return await this.apiService.updateVisit(entityId, data)
      
      case MutationType.DELETE:
        return await this.apiService.request(`/visits/${entityId}`, {
          method: 'DELETE'
        })
      
      default:
        throw new Error(`Type de mutation non supporté: ${type}`)
    }
  }

  /**
   * Synchronise une mutation d'action
   */
  async syncAction(mutation) {
    const { entityId, type, data } = mutation

    switch (type) {
      case MutationType.INSERT:
        return await this.apiService.request('/actions', {
          method: 'POST',
          body: JSON.stringify(data)
        })
      
      case MutationType.UPDATE:
        return await this.apiService.request(`/actions/${entityId}`, {
          method: 'PUT',
          body: JSON.stringify(data)
        })
      
      case MutationType.DELETE:
        return await this.apiService.request(`/actions/${entityId}`, {
          method: 'DELETE'
        })
      
      default:
        throw new Error(`Type de mutation non supporté: ${type}`)
    }
  }

  /**
   * Synchronise une mutation de contrôle
   */
  async syncControl(mutation) {
    const { entityId, type, data } = mutation

    switch (type) {
      case MutationType.INSERT:
        return await this.apiService.request('/controls', {
          method: 'POST',
          body: JSON.stringify(data)
        })
      
      case MutationType.UPDATE:
        return await this.apiService.request(`/controls/${entityId}`, {
          method: 'PUT',
          body: JSON.stringify(data)
        })
      
      case MutationType.DELETE:
        return await this.apiService.request(`/controls/${entityId}`, {
          method: 'DELETE'
        })
      
      default:
        throw new Error(`Type de mutation non supporté: ${type}`)
    }
  }

  /**
   * Synchronise une mutation de commentaire
   */
  async syncComment(mutation) {
    const { entityId, type, data } = mutation

    switch (type) {
      case MutationType.INSERT:
        return await this.apiService.request('/comments', {
          method: 'POST',
          body: JSON.stringify(data)
        })
      
      case MutationType.UPDATE:
        return await this.apiService.request(`/comments/${entityId}`, {
          method: 'PUT',
          body: JSON.stringify(data)
        })
      
      case MutationType.DELETE:
        return await this.apiService.request(`/comments/${entityId}`, {
          method: 'DELETE'
        })
      
      default:
        throw new Error(`Type de mutation non supporté: ${type}`)
    }
  }

  /**
   * Synchronise une mutation de pré-chiffrage
   */
  async syncPreChiffrage(mutation) {
    const { entityId, type, data } = mutation

    switch (type) {
      case MutationType.INSERT:
        return await this.apiService.request('/pre-chiffrages', {
          method: 'POST',
          body: JSON.stringify(data)
        })
      
      case MutationType.UPDATE:
        return await this.apiService.request(`/pre-chiffrages/${entityId}`, {
          method: 'PUT',
          body: JSON.stringify(data)
        })
      
      case MutationType.DELETE:
        return await this.apiService.request(`/pre-chiffrages/${entityId}`, {
          method: 'DELETE'
        })
      
      default:
        throw new Error(`Type de mutation non supporté: ${type}`)
    }
  }

  /**
   * Met à jour l'entité locale après synchronisation réussie
   */
  async updateLocalEntityAfterSync(mutation, apiResult) {
    const { entity, entityId } = mutation

    try {
      switch (entity) {
        case MutationEntity.PROPERTY_TECHNICAL_MANAGER:
        case MutationEntity.PROPERTY_PLANIFICATION: {
          // Mettre à jour la propriété pour supprimer le flag toSync
          const property = await this.dexieService.getProperty(entityId)
          if (property) {
            delete property.toSync
            delete property.lastSyncAttempt
            property.lastSynced = new Date().toISOString()
            await this.dexieService.addProperty(property)
          }
          break
        }

        case MutationEntity.VISIT: {
          // L'API de création de visite retourne la propriété complète mise à jour
          // avec scheduledVisitAt calculé et le tableau visits
          if (apiResult && apiResult.id && apiResult.visits) {
            // apiResult est la propriété complète
            await this.dexieService.addProperty(apiResult)
          }
          // Note: Les visites sont stockées dans property.visits, pas dans une table séparée
          break
        }

        case MutationEntity.PRE_CHIFFRAGE: {
          if (mutation.type === MutationType.DELETE) {
            // Après suppression sync : retirer le pré-chiffrage de property.preChiffrages localement
            // L'API ne retourne que { message: '...' }, pas la propriété complète
            // La smartSync déclenchée ensuite (via initializeApiStatusListener) récupérera la vraie liste,
            // mais on nettoie immédiatement pour éviter un affichage incohérent
            const propertyId = mutation.data?.propertyId
            if (propertyId) {
              const property = await this.dexieService.getProperty(propertyId)
              if (property && property.preChiffrages) {
                property.preChiffrages = property.preChiffrages.filter(
                  pc => pc.id !== entityId && pc.tempId !== entityId
                )
                delete property.toSync
                await this.dexieService.addProperty(property)
              }
            }
          } else if (apiResult && apiResult.id && apiResult.preChiffrages) {
            // CREATE ou UPDATE : l'API retourne la propriété complète mise à jour avec preChiffrages
            await this.dexieService.addProperty(apiResult)
          }
          // Note: Les pré-chiffrages sont stockés dans property.preChiffrages, pas dans une table séparée
          break
        }

        case MutationEntity.ACTION: {
          // Note: Les actions sont gérées via l'API, pas stockées localement
          // La propriété sera mise à jour via la synchronisation
          break
        }

        // Ajouter d'autres cas selon les besoins
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'entité locale:', error)
      // Ne pas propager l'erreur car la synchronisation API a réussi
    }
  }

  /**
   * Récupère les statistiques de synchronisation
   * @returns {Promise<Object>}
   */
  async getSyncStats() {
    try {
      const { mutations } = await import('./dexieService')
      
      // Toutes les mutations sont en attente (elles sont supprimées après sync)
      const totalMutations = await mutations().count()

      // Récupérer les propriétés avec flag toSync
      const propertiesWithToSync = await this.dexieService.properties()
        .filter(p => p.toSync === 1 || p.toSync === true).count()

      return {
        total: totalMutations,
        pending: totalMutations,
        synced: 0,
        propertiesWithToSync,
        syncInProgress: this.syncInProgress
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des stats de sync:', error)
      return {
        total: 0,
        pending: 0,
        synced: 0,
        propertiesWithToSync: 0,
        syncInProgress: false
      }
    }
  }

  /**
   * Ajoute un listener pour les événements de synchronisation
   * @param {Function} callback - Fonction appelée lors des événements de sync
   * @returns {Function} Fonction pour supprimer le listener
   */
  addSyncListener(callback) {
    this.syncListeners.push(callback)
    
    return () => {
      const index = this.syncListeners.indexOf(callback)
      if (index > -1) {
        this.syncListeners.splice(index, 1)
      }
    }
  }

  /**
   * Notifie tous les listeners des événements de synchronisation
   */
  notifySyncListeners(event) {
    this.syncListeners.forEach(listener => {
      try {
        listener(event)
      } catch (error) {
        console.error('Erreur dans le listener de sync:', error)
      }
    })
  }

  /**
   * Force la synchronisation (peut être appelé par un bouton utilisateur)
   */
  async forceSynchronization() {
    return await this.syncPendingMutations(true)
  }

  /**
   * Traite les attributions multiples (pour plusieurs lots)
   * @param {Array} lotIds - IDs des lots/propriétés
   * @param {string} managerId - ID du gestionnaire
   * @param {string} teamId - ID de l'équipe
   * @returns {Promise<{success: boolean, results: Array, synced: boolean}>}
   */
  async processMultipleAttributions(lotIds, managerId, teamId) {
    try {
      const results = []
      let allSynced = true

      // Traiter chaque lot individuellement
      for (const lotId of lotIds) {
        try {
          const result = await this.executeMutation({
            entity: MutationEntity.PROPERTY_TECHNICAL_MANAGER,
            type: MutationType.UPDATE,
            entityId: lotId,
            data: {
              technicalManager: { id: managerId },
              team: teamId ? { id: teamId } : null
            },
            apiCall: async () => {
              return await this.apiService.request(`/properties/${lotId}/technical-manager`, {
                method: 'PUT',
                body: JSON.stringify({
                  technicalManagerId: managerId,
                  teamId: teamId
                })
              })
            },
            localUpdate: async (apiResult, toSync) => {
              const property = await this.dexieService.getProperty(lotId)
              if (property) {
                property.technicalManager = managerId ? { id: managerId } : null
                property.teams = teamId ? [{ id: teamId }] : []
                
                if (toSync) {
                  property.toSync = 1
                  property.lastSyncAttempt = new Date().toISOString()
                } else {
                  delete property.toSync
                  delete property.lastSyncAttempt
                  property.lastSynced = new Date().toISOString()
                }
                
                await this.dexieService.addProperty(property)
              }
            }
          })

          results.push({
            lotId,
            success: result.success,
            synced: result.synced,
            error: result.error
          })

          if (!result.synced) {
            allSynced = false
          }

        } catch (error) {
          console.error(`Erreur lors du traitement du lot ${lotId}:`, error)
          results.push({
            lotId,
            success: false,
            synced: false,
            error: error.message
          })
          allSynced = false
        }
      }

      const successCount = results.filter(r => r.success).length
      const overallSuccess = successCount === lotIds.length

      return {
        success: overallSuccess,
        results,
        synced: allSynced,
        successCount,
        totalCount: lotIds.length
      }

    } catch (error) {
      console.error('Erreur lors du traitement des attributions multiples:', error)
      return {
        success: false,
        results: [],
        synced: false,
        error: error.message
      }
    }
  }

  /**
   * Met à jour la date de planification d'une propriété
   * @param {string} propertyId - ID de la propriété
   * @param {string} planifiedAt - Date de planification
   * @returns {Promise<{success: boolean, synced: boolean, error?: string}>}
   */
  async updatePropertyPlanification(propertyId, planifiedAt) {
    return await this.executeMutation({
      entity: MutationEntity.PROPERTY_PLANIFICATION,
      type: MutationType.UPDATE,
      entityId: propertyId,
      data: {
        planifiedAt: planifiedAt
      },
      apiCall: async () => {
        return await this.apiService.request(`/properties/${propertyId}/planification`, {
          method: 'PUT',
          body: JSON.stringify({
            planifiedAt: planifiedAt
          })
        })
      },
      localUpdate: async (apiResult, toSync) => {
        const property = await this.dexieService.getProperty(propertyId)
        if (property) {
          property.planifiedAt = planifiedAt
          property.updatedAt = new Date().toISOString()
          
          if (toSync) {
            property.toSync = 1
            property.lastSyncAttempt = new Date().toISOString()
          } else {
            delete property.toSync
            delete property.lastSyncAttempt
            property.lastSynced = new Date().toISOString()
          }
          
          await this.dexieService.addProperty(property)
        }
      }
    })
  }

  /**
   * Met à jour le gestionnaire technique d'une propriété
   * @param {string} propertyId - ID de la propriété
   * @param {string} managerId - ID du gestionnaire
   * @param {string} teamId - ID de l'équipe (optionnel)
   * @returns {Promise<{success: boolean, synced: boolean, error?: string}>}
   */
  async updatePropertyTechnicalManager(propertyId, managerId, teamId = null) {
    return await this.executeMutation({
      entity: MutationEntity.PROPERTY_TECHNICAL_MANAGER,
      type: MutationType.UPDATE,
      entityId: propertyId,
      data: {
        technicalManager: managerId ? { id: managerId } : null,
        team: teamId ? { id: teamId } : null
      },
      apiCall: async () => {
        return await this.apiService.request(`/properties/${propertyId}/technical-manager`, {
          method: 'PUT',
          body: JSON.stringify({
            technicalManagerId: managerId,
            teamId: teamId
          })
        })
      },
      localUpdate: async (apiResult, toSync) => {
        const property = await this.dexieService.getProperty(propertyId)
        if (property) {
          property.technicalManager = managerId ? { id: managerId } : null
          property.teams = teamId ? [{ id: teamId }] : []
          property.updatedAt = new Date().toISOString()
          
          if (toSync) {
            property.toSync = 1
            property.lastSyncAttempt = new Date().toISOString()
          } else {
            delete property.toSync
            delete property.lastSyncAttempt
            property.lastSynced = new Date().toISOString()
          }
          
          await this.dexieService.addProperty(property)
        }
      }
    })
  }

  /**
   * Récupère l'historique des mutations pour une propriété
   * @param {string} propertyId - ID de la propriété
   * @returns {Promise<Array>}
   */
  async getPropertyMutationHistory(propertyId) {
    try {
      const { mutations } = await import('./dexieService')
      
      const mutationHistory = await mutations()
        .where('propertyId')
        .equals(propertyId)
        .reverse()
        .sortBy('insertedAt')
      
      return mutationHistory

    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique des mutations:', error)
      return []
    }
  }

  /**
   * Récupère toutes les mutations récentes
   * @param {number} limit - Nombre maximum de mutations à récupérer
   * @returns {Promise<Array>}
   */
  async getRecentMutations(limit = 50) {
    try {
      const { mutations } = await import('./dexieService')
      
      const recentMutations = await mutations()
        .reverse()
        .sortBy('insertedAt')
      
      return recentMutations.slice(0, limit)

    } catch (error) {
      console.error('Erreur lors de la récupération des mutations récentes:', error)
      return []
    }
  }

  /**
   * Récupère les mutations par gestionnaire
   * @param {string} managerId - ID du gestionnaire
   * @param {string} role - 'previous' ou 'new' pour filtrer par ancien ou nouveau gestionnaire
   * @returns {Promise<Array>}
   */
  async getMutationsByManager(managerId, role = 'new') {
    try {
      const { mutations } = await import('./dexieService')
      
      const allMutations = await mutations().toArray()
      
      const filtered = allMutations.filter(mutation => {
        if (role === 'previous') {
          return mutation.data?.previousManagerId === managerId
        } else {
          return mutation.data?.newManagerId === managerId || 
                 mutation.data?.technicalManagerId === managerId
        }
      })
      
      return filtered.sort((a, b) => new Date(b.insertedAt) - new Date(a.insertedAt))

    } catch (error) {
      console.error('Erreur lors de la récupération des mutations par gestionnaire:', error)
      return []
    }
  }

  /**
   * Récupère les statistiques des mutations
   * @returns {Promise<object>}
   */
  async getMutationStats() {
    try {
      const { mutations } = await import('./dexieService')
      
      const totalMutations = await mutations().count()
      const allMutations = await mutations().toArray()
      
      // Compter les mutations par type
      const mutationsByType = allMutations.reduce((acc, mutation) => {
        acc[mutation.type] = (acc[mutation.type] || 0) + 1
        return acc
      }, {})

      // Compter les mutations par entité
      const mutationsByEntity = allMutations.reduce((acc, mutation) => {
        acc[mutation.entity] = (acc[mutation.entity] || 0) + 1
        return acc
      }, {})

      return {
        total: totalMutations,
        byType: mutationsByType,
        byEntity: mutationsByEntity,
        lastMutationDate: allMutations.length > 0 ? 
          allMutations.sort((a, b) => new Date(b.insertedAt) - new Date(a.insertedAt))[0].insertedAt : 
          null
      }

    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des mutations:', error)
      return {
        total: 0,
        byType: {},
        byEntity: {},
        lastMutationDate: null
      }
    }
  }

  /**
   * Enregistre la création d'une visite dans l'historique des mutations
   * @param {object} visitData - Données de la visite créée
   * @param {string} propertyId - ID de la propriété associée
   * @returns {Promise<boolean>}
   */
  async recordVisitCreation(visitData, propertyId) {
    try {
      const { mutations } = await import('./dexieService')
      
      // Récupérer les informations de la propriété
      let propertyInfo = null
      if (propertyId) {
        try {
          propertyInfo = await this.dexieService.getProperty(propertyId)
        } catch (error) {
          console.warn('Impossible de récupérer les infos de la propriété:', error)
        }
      }

      const visitId = visitData.id || visitData.tempId
      const finalPropertyId = propertyId || visitData.property?.id
      
      // Générer un ID unique pour la mutation
      const mutationId = this.generateMutationId('visit', 'INSERT', visitId)
      
      // Préparer les données de la mutation
      const mutationData = {
        visitId: visitId,
        propertyId: finalPropertyId,
        propertyNsil: propertyInfo?.nsil || visitData.property?.nsil,
        visitType: visitData.type || null,
        visitDate: visitData.date || visitData.scheduledAt || null,
        visitStatus: visitData.status || null,
        technicalManagerId: propertyInfo?.technicalManager?.id || null,
        technicalManagerName: propertyInfo?.technicalManager?.fullName || null,
        teamId: propertyInfo?.teams?.[0]?.id || null,
        propertyData: propertyInfo ? {
          nsil: propertyInfo.nsil,
          address: propertyInfo.address,
          type: propertyInfo.type
        } : null,
        visitData: {
          type: visitData.type,
          date: visitData.date || visitData.scheduledAt,
          status: visitData.status,
          comment: visitData.comment
        }
      }
      
      // Format uniformisé
      const mutation = {
        attempts: 0,
        data: this.deepSerialize(mutationData),
        entity: 'visit',
        entityId: visitId,
        insertedAt: new Date().toISOString(),
        lastAttempt: null,
        lastError: null,
        mutationId: mutationId,
        synced: false,
        type: 'INSERT',
        propertyId: finalPropertyId
      }

      // Enregistrer la mutation dans IndexedDB
      await mutations().add(mutation)
      
      return true

    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la mutation de visite:', error)
      // Ne pas bloquer la création de visite si l'enregistrement de la mutation échoue
      return false
    }
  }

  /**
   * Récupère l'historique des visites pour une propriété
   * @param {string} propertyId - ID de la propriété
   * @returns {Promise<Array>}
   */
  async getPropertyVisitsHistory(propertyId) {
    try {
      const { mutations } = await import('./dexieService')
      
      const visitsHistory = await mutations()
        .where('propertyId')
        .equals(propertyId)
        .filter(m => m.entity === 'visit')
        .reverse()
        .sortBy('insertedAt')
      
      return visitsHistory

    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique des visites:', error)
      return []
    }
  }

  /**
   * Récupère toutes les visites créées récemment
   * @param {number} limit - Nombre maximum de visites à récupérer
   * @returns {Promise<Array>}
   */
  async getRecentVisits(limit = 50) {
    try {
      const { mutations } = await import('./dexieService')
      
      const recentVisits = await mutations()
        .filter(m => m.entity === 'visit')
        .reverse()
        .sortBy('insertedAt')
      
      return recentVisits.slice(0, limit)

    } catch (error) {
      console.error('Erreur lors de la récupération des visites récentes:', error)
      return []
    }
  }

  /**
   * Crée un pré-chiffrage avec support du mode hors ligne
   * @param {Object} preChiffrageData - Données du pré-chiffrage
   * @param {string} propertyId - ID de la propriété associée
   * @returns {Promise<{success: boolean, synced: boolean, error?: string}>}
   */
  async createPreChiffrage(preChiffrageData, propertyId) {
    // Générer un ID temporaire pour le pré-chiffrage
    const tempId = `temp-prechiffrage-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    
    return await this.executeMutation({
      entity: MutationEntity.PRE_CHIFFRAGE,
      type: MutationType.INSERT,
      entityId: tempId,
      data: {
        ...preChiffrageData,
        propertyId: propertyId,
        tempId: tempId
      },
      apiCall: async () => {
        return await this.apiService.request('/pre-chiffrages', {
          method: 'POST',
          body: JSON.stringify(preChiffrageData)
        })
      },
      localUpdate: async (apiResult, toSync) => {
        // L'API de création de pré-chiffrage retourne la propriété complète mise à jour
        // avec le tableau preChiffrages
        if (apiResult && apiResult.id && apiResult.preChiffrages) {
          // apiResult est la propriété complète - remplacer localement
          await this.dexieService.addProperty(apiResult)
        } else if (toSync) {
          // ─── Mode hors ligne ────────────────────────────────────────────────
          // Si des lignes CAT et/ou GPR sont présentes, créer un objet local
          // par type de ligne pour refléter la même séparation qu'en base.
          // ────────────────────────────────────────────────────────────────────
          const property = await this.dexieService.getProperty(propertyId)
          if (property) {
            if (!property.preChiffrages) {
              property.preChiffrages = []
            }

            const generalFields = preChiffrageData.generalFields || {}
            const catLines      = preChiffrageData.lines || []
            const gprLines      = preChiffrageData.gprLines || []
            const now           = new Date().toISOString()

            // Objet de base commun aux deux types
            const baseLocal = {
              commentairesGeneraux: generalFields.commentairesGeneraux || null,
              chiffrageTermine:     generalFields.chiffrageTermine     || null,
              photosEnregistrees:   generalFields.photosEnregistrees   || null,
              createdAt:            now,
              updatedAt:            null,
              toSync:               1,
            }

            // Créer un enregistrement local pour les lignes CAT (si présentes)
            if (catLines.length > 0) {
              const tempIdCat = `${tempId}-cat`
              property.preChiffrages.unshift({
                ...baseLocal,
                id:        tempIdCat,
                tempId:    tempIdCat,
                lines:     catLines.map(l => ({ ...l, lineType: 'CAT' })),
                isGpr:     false,
                lineCount: catLines.length,
              })
            }

            // Créer un enregistrement local pour les lignes GPR (si présentes)
            if (gprLines.length > 0) {
              const tempIdGpr = `${tempId}-gpr`
              property.preChiffrages.unshift({
                ...baseLocal,
                id:        tempIdGpr,
                tempId:    tempIdGpr,
                lines:     gprLines.map(l => ({ ...l, lineType: 'GPR' })),
                isGpr:     true,
                lineCount: gprLines.length,
              })
            }

            property.toSync = 1
            property.lastSyncAttempt = now
            await this.dexieService.addProperty(property)
          }
        }
      }
    })
  }

  /**
   * Met à jour un pré-chiffrage avec support du mode hors ligne
   * @param {string} preChiffrageId - ID du pré-chiffrage
   * @param {Object} preChiffrageData - Données du pré-chiffrage
   * @param {string} propertyId - ID de la propriété associée (requis pour le mode hors ligne)
   * @returns {Promise<{success: boolean, synced: boolean, error?: string}>}
   */
  async updatePreChiffrage(preChiffrageId, preChiffrageData, propertyId = null) {
    return await this.executeMutation({
      entity: MutationEntity.PRE_CHIFFRAGE,
      type: MutationType.UPDATE,
      entityId: preChiffrageId,
      data: {
        ...preChiffrageData,
        propertyId: propertyId
      },
      apiCall: async () => {
        return await this.apiService.request(`/pre-chiffrages/${preChiffrageId}`, {
          method: 'PUT',
          body: JSON.stringify(preChiffrageData)
        })
      },
      localUpdate: async (apiResult, toSync) => {
        if (apiResult && apiResult.id && apiResult.preChiffrages) {
          // API a retourné la propriété complète - mettre à jour localement
          await this.dexieService.addProperty(apiResult)
        } else if (toSync && propertyId) {
          // Mode hors ligne - mettre à jour le pré-chiffrage dans property.preChiffrages
          const property = await this.dexieService.getProperty(propertyId)
          if (property && property.preChiffrages) {
            const pcIndex = property.preChiffrages.findIndex(pc => pc.id === preChiffrageId)
            if (pcIndex !== -1) {
              const updatedLines    = preChiffrageData.lines    || property.preChiffrages[pcIndex].lines    || []
              const updatedGprLines = preChiffrageData.gprLines || property.preChiffrages[pcIndex].gprLines || []
              // Recalculer isGpr : on se base sur lineType des nouvelles lignes
              const allNewLines = [...updatedLines, ...updatedGprLines]
              const newIsGpr = allNewLines.length > 0 && allNewLines.every(l => l.lineType === 'GPR')
              property.preChiffrages[pcIndex] = {
                ...property.preChiffrages[pcIndex],
                commentairesGeneraux: preChiffrageData.generalFields?.commentairesGeneraux ?? property.preChiffrages[pcIndex].commentairesGeneraux,
                chiffrageTermine:     preChiffrageData.generalFields?.chiffrageTermine     ?? property.preChiffrages[pcIndex].chiffrageTermine,
                photosEnregistrees:   preChiffrageData.generalFields?.photosEnregistrees   ?? property.preChiffrages[pcIndex].photosEnregistrees,
                lines:     updatedLines,
                gprLines:  updatedGprLines,
                isGpr:     newIsGpr,
                lineCount: allNewLines.length,
                updatedAt: new Date().toISOString(),
                toSync:    1,
              }
              property.toSync = 1
              await this.dexieService.addProperty(property)
            }
          }
        }
      }
    })
  }

  /**
   * Supprime un pré-chiffrage avec support du mode hors ligne
   * @param {string} preChiffrageId - ID du pré-chiffrage
   * @param {string} propertyId - ID de la propriété associée (requis pour le mode hors ligne)
   * @returns {Promise<{success: boolean, synced: boolean, error?: string}>}
   */
  async deletePreChiffrage(preChiffrageId, propertyId = null) {
    return await this.executeMutation({
      entity: MutationEntity.PRE_CHIFFRAGE,
      type: MutationType.DELETE,
      entityId: preChiffrageId,
      data: {
        propertyId: propertyId // Conserver pour pouvoir nettoyer localement après sync
      },
      apiCall: async () => {
        return await this.apiService.request(`/pre-chiffrages/${preChiffrageId}`, {
          method: 'DELETE'
        })
      },
      localUpdate: async (apiResult, toSync) => {
        // Supprimer le pré-chiffrage de property.preChiffrages localement
        const pid = propertyId
        if (pid) {
          try {
            const property = await this.dexieService.getProperty(pid)
            if (property && property.preChiffrages) {
              if (toSync) {
                // Mode hors ligne : marquer comme à supprimer
                const pcIndex = property.preChiffrages.findIndex(pc => pc.id === preChiffrageId)
                if (pcIndex !== -1) {
                  property.preChiffrages[pcIndex].toDelete = 1
                  property.preChiffrages[pcIndex].toSync = 1
                }
              } else {
                // En ligne : retirer directement de la liste
                property.preChiffrages = property.preChiffrages.filter(pc => pc.id !== preChiffrageId)
              }
              await this.dexieService.addProperty(property)
            }
          } catch (error) {
            console.error('Erreur lors de la suppression locale du pré-chiffrage:', error)
          }
        }
      }
    })
  }

  /**
   * Récupère l'historique des pré-chiffrages pour une propriété
   * @param {string} propertyId - ID de la propriété
   * @returns {Promise<Array>}
   */
  async getPropertyPreChiffragesHistory(propertyId) {
    try {
      const { mutations } = await import('./dexieService')
      
      const preChiffragesHistory = await mutations()
        .where('propertyId')
        .equals(propertyId)
        .filter(m => m.entity === 'pre_chiffrage')
        .reverse()
        .sortBy('insertedAt')
      
      return preChiffragesHistory

    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique des pré-chiffrages:', error)
      return []
    }
  }

  /**
   * Récupère tous les pré-chiffrages créés récemment
   * @param {number} limit - Nombre maximum de pré-chiffrages à récupérer
   * @returns {Promise<Array>}
   */
  async getRecentPreChiffrages(limit = 50) {
    try {
      const { mutations } = await import('./dexieService')
      
      const recentPreChiffrages = await mutations()
        .filter(m => m.entity === 'pre_chiffrage')
        .reverse()
        .sortBy('insertedAt')
      
      return recentPreChiffrages.slice(0, limit)

    } catch (error) {
      console.error('Erreur lors de la récupération des pré-chiffrages récents:', error)
      return []
    }
  }
}

export default new MutationService()
